import {ControlContainer, ControlValueAccessor, FormControl} from "@angular/forms";
import {
    ChangeDetectorRef, Directive,
    EventEmitter,
    InjectionToken,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import {DataResourceModel} from "@fedeltapointofsale/data-client";
import {Subscription} from "rxjs";
import {EditService} from "../edit-page/edit.service";
import {EntityFormControl} from "../entity-form-builder/entity-form-control";
import {EditMode} from "../edit-page/edit-mode";
import {EntityFormGroup} from "../entity-form-builder/entity-form-group";

export const EDIT_CONTROL_LABEL_CONTEXT = new InjectionToken<string>("EDIT_CONTROL_LABEL_CONTEXT");

/**
 * Abstract class that implements methods for the edit inputs
 */
@Directive()
export class AbstractEditControlComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
    @Input() formControl: FormControl;
    @Input() public label: string;
    @Input() public i18nParams: any;
    @Input() public placeholder: string;
    @Input() public elementId: string;
    /**
     * Set the control to be readonly on the EditMode is Edit
     */
    @Input() public readonlyOnEdit: boolean;

    /**
     * Override the default required that is managed by the validationf framework
     */
    @Input() public overrideRequired: boolean;

    /**
     * Override the default readonly. Can only make readonly, cannot make not-readonly if the model is readonly.
     */
    @Input() public readonly: boolean;

    /**
     * It doesn't rely on the dataResourceModel to check for readonly
     */
    @Input() public standalone: boolean;

    @Output() valueChange: EventEmitter<any> = new EventEmitter();

    @Output() public focussed: EventEmitter<boolean> = new EventEmitter();

    public _value: any;

    @Input() public formControlName: string;
    public editService: EditService;
    public dataResourceModel: DataResourceModel<any>;
    protected subscriptions: Subscription = new Subscription();
    private controlContainer: ControlContainer;
    protected changeDetectorRef: ChangeDetectorRef;
    private labelContext: string;

    constructor(injector: Injector) {
        if (injector) {
            this.changeDetectorRef = injector.get(ChangeDetectorRef, null);
            this.controlContainer = injector.get(ControlContainer, null);
            this.editService = injector.get(EditService, null);
            this.dataResourceModel = injector.get(DataResourceModel, null);
            this.labelContext = injector.get(EDIT_CONTROL_LABEL_CONTEXT, null);
        }
    }

    get isReadonly(): boolean {
        return this.readonly ||
            (!this.standalone && this.dataResourceModel && this.dataResourceModel.readonly) ||
            (this.readonlyOnEdit && this.editService.editMode === EditMode.EDIT) ||
            (this.formControl && this.formControl instanceof EntityFormControl && this.formControl.restricted);
    }

    ngOnInit(): void {
        let fieldPath: string = this.formControlName;
        if (this.controlContainer && this.formControlName && !this.formControl) {
            this.formControl = <FormControl>this.controlContainer.control.get(<string>this.formControlName);
            if (this.formControl instanceof EntityFormControl) {
                const entityFormControl: EntityFormControl = <EntityFormControl>this.formControl;

                this.writeValue(this.formControl.value);

                this.editService.registerFormControl(this.formControl);

                this.subscriptions.add(this.editService.editMode$.subscribe(() => this.changeDetectorRef.detectChanges()));

                this.subscriptions.add(this.formControl.$statusChange.subscribe(() => this.changeDetectorRef.detectChanges()));

                this.subscriptions.add(this.dataResourceModel.$readonlyChange.subscribe(() => this.changeDetectorRef.detectChanges()));

                if (this.overrideRequired != undefined) {
                    entityFormControl.overrideRequired = this.overrideRequired;
                }

                fieldPath = entityFormControl.path;
            } else if (this.formControl instanceof EntityFormGroup) {
                const entityFormGroup: EntityFormGroup = <EntityFormGroup>this.formControl;
                fieldPath = entityFormGroup.path;
            }
        }
        if (!this.labelContext && this.dataResourceModel) {
            this.labelContext = this.dataResourceModel.getResource();
        }
        if (fieldPath) {
            if (!this.label) {
                this.label = "wbo." + this.labelContext + ".edit.field." + fieldPath + ".label";
            }

            if (!this.placeholder) {
                this.placeholder = "wbo." + this.labelContext + ".edit.field." + fieldPath + ".placeholder";
            }

            if (!this.elementId) {
                this.elementId = "edit-control-" + fieldPath.replace(/\./g, "-");
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.formControl && this.editService) {
            this.editService.deregisterFormControl(this.formControl);
        }
    }

    /**
     *
     */
    propagateChange: any = () => {
    };

    /**
     *
     * @returns {any}
     */
    get value(): any {
        return this._value;
    }

    /**
     *
     * @param value
     */
    @Input()
    set value(value: any) {
        if (value !== this._value) {
            this.writeValue(value);
            this.propagateChange(value);
            this.valueChange.emit(value);
        }
    }

    /**
     *
     * @param obj
     */
    writeValue(obj: any): void {
        this._value = obj;
    }

    /**
     *
     * @param fn
     */
    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    /**
     *
     * @param fn
     */
    registerOnTouched(fn: any): void {
    }

    /**
     *
     * @param isDisabled
     */
    setDisabledState(isDisabled: boolean): void {
    }

    onFocus(focussed: boolean): void {
        this.focussed.emit(focussed);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["overrideRequired"]) {
            if (this.overrideRequired != undefined && this.formControl instanceof EntityFormControl) {
                this.formControl.overrideRequired = this.overrideRequired;
            }
        }
    }
}
